<template>
  <div class="complaint-type-container">
    <div class="page-content">
      <!-- 页面标题 -->
      <div class="page-title">{{ $t('complaintType.title') }}</div>

      <!-- 工具栏 -->
      <div class="toolbar">
        <el-button class="add-btn" @click="_openAddComplaintTypeModal">
          <i class="el-icon-plus"></i>
          {{ $t('complaintType.add') }}
        </el-button>
        <el-button class="icon-reset-btn" @click="handleReset">
          <i class="el-icon-refresh-right"></i>
        </el-button>

        <div class="toolbar-right">
          <el-input
            v-model="complaintTypeInfo.conditions.communityName"
            :placeholder="$t('complaintType.search.communityPlaceholder')"
            class="search-input"
            clearable
          />
          <el-button class="search-btn" @click="_queryComplaintTypeMethod">
            <i class="el-icon-search"></i>
            {{ $t('common.search') }}
          </el-button>
        </div>
      </div>

      <!-- 列表 -->
      <el-table
        :data="complaintTypeInfo.complaintTypes"
        stripe
        class="data-table"
        v-loading="loading"
      >
        <el-table-column
          prop="communityName"
          :label="$t('complaintType.communityName')"
          align="center"
          min-width="211"
        />
        <el-table-column
          prop="typeName"
          :label="$t('complaintType.typeName')"
          align="center"
          min-width="244"
        />
        <el-table-column
          prop="notifyWay"
          :label="$t('complaintType.notifyWay')"
          align="center"
          min-width="242"
        >
          <template slot-scope="scope">
            <span v-if="scope.row.notifyWay === 'SMS'">{{ $t('complaintType.sms') }}</span>
            <span v-else-if="scope.row.notifyWay === 'WORK_LICENSE'">{{ $t('complaintType.wechatWorkLicense') }}</span>
            <span v-else>{{ $t('complaintType.wechat') }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="appraiseReply"
          :label="$t('complaintType.appraiseReply')"
          align="center"
          min-width="288"
        >
          <template slot-scope="scope">
            {{ scope.row.appraiseReply === 'Y' ? $t('complaintType.autoReply') : $t('complaintType.manualReply') }}
          </template>
        </el-table-column>
        <el-table-column
          prop="staffs"
          :label="$t('complaintType.handler')"
          align="center"
          min-width="198"
        >
          <template slot-scope="scope">
            <div v-for="(item, index) in scope.row.staffs" :key="index">{{ item.staffName }}</div>
          </template>
        </el-table-column>
        <el-table-column
          prop="createTime"
          :label="$t('complaintType.createTime')"
          align="center"
          min-width="221"
        />
        <el-table-column
          :label="$t('common.operation')"
          align="center"
          width="176"
          fixed="right"
        >
          <template slot-scope="scope">
            <el-button type="text" class="edit-btn" @click="_openEditComplaintTypeModel(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button type="text" class="delete-btn" @click="_openDeleteComplaintTypeModel(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="page.current"
        :page-sizes="[10, 20, 30, 50]"
        :page-size="page.size"
        layout="total, sizes, prev, pager, next, jumper"
        :total="complaintTypeInfo.total"
        class="pagination-bar"
      />
    </div>

    <delete-complaint-type ref="deleteComplaintType" @success="handleSuccess" />
    <add-complaint-type-modal ref="addComplaintTypeModal" @success="handleSuccess" />
    <edit-complaint-type-modal ref="editComplaintTypeModal" @success="handleSuccess" />
  </div>
</template>

<script>
import { listComplaintType } from '@/api/oa/complaintTypeApi'
import DeleteComplaintType from '@/components/oa/deleteComplaintType'
import AddComplaintTypeModal from '@/components/oa/AddComplaintTypeModal'
import EditComplaintTypeModal from '@/components/oa/EditComplaintTypeModal'
import { getEffectiveCommunityId, getMyEnteredCommunitys } from '@/api/community/communityApi'

export default {
  name: 'ComplaintTypeList',
  components: {
    DeleteComplaintType,
    AddComplaintTypeModal,
    EditComplaintTypeModal
  },
  data() {
    return {
      loading: false,
      complaintTypeInfo: {
        complaintTypes: [],
        total: 0,
        conditions: {
          typeCd: '',
          typeName: '',
          notifyWay: '',
          communityName: ''
        }
      },
      page: {
        current: 1,
        size: 10
      },
      authorizedCommunityIds: []
    }
  },
  async created() {
    this.communityId = ''
    await this.loadAuthorizedCommunitys()
    this._listComplaintTypes()
  },
  methods: {
    async loadAuthorizedCommunitys() {
      try {
        const res = await getMyEnteredCommunitys({ page: 1, row: 1000 })
        this.authorizedCommunityIds = (res.communitys || [])
          .map(item => getEffectiveCommunityId(item.communityId))
          .filter(Boolean)
      } catch (error) {
        this.authorizedCommunityIds = []
      }
    },
    async _listComplaintTypes() {
      try {
        this.loading = true
        const params = {
          page: this.page.current,
          row: this.page.size,
          communityId: '',
          communityIds: this.authorizedCommunityIds,
          ...this.complaintTypeInfo.conditions
        }
        const { data, total } = await listComplaintType(params)
        this.complaintTypeInfo.complaintTypes = data
        this.complaintTypeInfo.total = total
      } catch (error) {
        this.$message.error(this.$t('complaintType.fetchError'))
      } finally {
        this.loading = false
      }
    },
    _queryComplaintTypeMethod() {
      this.page.current = 1
      this._listComplaintTypes()
    },
    handleReset() {
      this.complaintTypeInfo.conditions = {
        typeCd: '',
        typeName: '',
        notifyWay: '',
        communityName: ''
      }
      this.page.current = 1
      this._listComplaintTypes()
    },
    _openAddComplaintTypeModal() {
      this.$refs.addComplaintTypeModal.open()
    },
    _openEditComplaintTypeModel(complaintType) {
      this.$refs.editComplaintTypeModal.open(complaintType)
    },
    _openDeleteComplaintTypeModel(complaintType) {
      this.$refs.deleteComplaintType.open(complaintType)
    },
    handleSuccess() {
      this._listComplaintTypes()
    },
    handleSizeChange(val) {
      this.page.size = val
      this._listComplaintTypes()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this._listComplaintTypes()
    }
  }
}
</script>

<style lang="scss" scoped>
.complaint-type-container {
  padding: 20px;
  background-color: #EBF2FA;
  min-height: calc(100vh - 84px);
  font-family: 'PingFang SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;

  .page-content {
    background: #FFFFFF;
    border-radius: 12px;
    padding: 22px 24px;
  }

  .page-title {
    font-size: 16px;
    font-family: 'PingFang SC', sans-serif;
    font-weight: 600;
    color: #1D2129;
    padding-bottom: 20px;
    border-bottom: 1px solid #DCE6F5;
    margin-bottom: 20px;
    text-align: left;
  }

  .toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;

    .add-btn {
      width: 141px;
      height: 38px;
      border-radius: 10px;
      background: #0E76FF;
      border: none;
      color: #FFFFFF;
      font-family: 'Inter', 'PingFang SC', sans-serif;
      font-weight: 500;
      font-size: 14px;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;

      &:hover {
        background: #1C99FF;
      }

      i {
        font-size: 12px;
      }
    }

    .icon-reset-btn {
      width: 38px;
      height: 38px;
      border-radius: 10px;
      border: 1px solid #DCE6F5;
      background: #FFFFFF;
      color: #8C9BB4;
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;

      i {
        font-size: 14px;
      }

      &:hover {
        color: #0E76FF;
        border-color: #0E76FF;
      }
    }

    .toolbar-right {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-left: auto;

      .search-input {
        width: 256px;

        ::v-deep .el-input__inner {
          border-radius: 10px;
          border: 1px solid #DCE6F5;
          background: #FFFFFF;
          font-family: 'Inter', 'PingFang SC', sans-serif;
          font-size: 14px;
          color: #1D2129;
          height: 38px;
          line-height: 38px;

          &::placeholder {
            color: #8C9BB4;
          }

          &:focus {
            border-color: #0E76FF;
          }
        }
      }

      .search-btn {
        width: 84px;
        height: 38px;
        border-radius: 10px;
        background: #FFFFFF;
        border: 1px solid #0E76FF;
        color: #0E76FF;
        font-family: 'Inter', 'PingFang SC', sans-serif;
        font-weight: 500;
        font-size: 14px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;

        &:hover {
          background: #E8F3FF;
        }

        i {
          font-size: 12px;
        }
      }
    }
  }

  .data-table {
    border-radius: 10px;
    overflow: hidden;

    &::before {
      display: none;
    }

    ::v-deep .el-table__header-wrapper {
      th {
        background-color: #EBF2FA !important;
        color: #364153 !important;
        font-weight: 500;
        font-size: 14px;
        font-family: 'PingFang SC', sans-serif;
        padding: 12px 0;
        height: 50px;
      }
    }

    ::v-deep .el-table__row--striped td {
      background-color: #F8FBFF !important;
    }

    ::v-deep .el-table__body tr:hover > td {
      background-color: #E8F3FF !important;
    }

    ::v-deep td {
      color: #1D2129;
      font-size: 14px;
      font-family: 'PingFang SC', sans-serif;
      padding: 12px 0;
      height: 50px;
    }

    ::v-deep .edit-btn,
    ::v-deep .delete-btn {
      border-radius: 0;
      background: transparent;
      border: none;
      padding: 0 8px;
      color: #0E76FF;
      font-size: 14px;
      font-family: 'PingFang SC', sans-serif;
      font-weight: 500;

      &:hover {
        color: #1C99FF;
        background: transparent;
      }
    }

    ::v-deep .delete-btn {
      color: #FB2C36;

      &:hover {
        color: #ff4d4f;
      }
    }
  }

  .pagination-bar {
    margin-top: 20px;
    text-align: right;

    ::v-deep .el-pagination__total,
    ::v-deep .el-pagination__jump {
      color: #1D2129;
      font-size: 14px;
      font-family: 'PingFang SC', sans-serif;
    }

    ::v-deep .el-pager li {
      border-radius: 2px;
      width: 32px;
      height: 32px;
      line-height: 32px;

      &.active {
        background-color: #E8F3FF !important;
        color: #0E76FF !important;
        border-radius: 8px;
        font-family: 'Nunito Sans', sans-serif;
        font-weight: 600;
      }
    }

    ::v-deep .el-pagination__sizes .el-input__inner {
      background: #F2F3F5;
      border-radius: 8px;
      border: none;
      height: 32px;
      line-height: 32px;
    }

    ::v-deep .btn-prev,
    ::v-deep .btn-next {
      width: 32px;
      height: 32px;
      line-height: 32px;
      border-radius: 2px;
    }
  }
}
</style>
